import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Loader2, PlugZap, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { fmtDateTime, fmtRelative } from '../lib/format';
import { cbApi, resolveSyncError, useConnectedBusinessesEnabled } from '../lib/connectedBusinesses';

/** Owner-only gate. Redirects to /home with a toast otherwise. */
function useOwnerGate() {
  const { user } = useAuth();
  const enabled = useConnectedBusinessesEnabled();
  const navigate = useNavigate();
  const toast = useToast();
  const fired = useRef(false);
  const isOwner = String(user?.role || '').toLowerCase() === 'owner';
  const allowed = enabled === true && isOwner;

  useEffect(() => {
    if (enabled === null || allowed || fired.current) return;
    fired.current = true;
    toast.show(enabled === false ? 'Not available for this tenant' : 'Only the business owner can manage Connected Businesses', 'error');
    navigate('/home', { replace: true });
  }, [enabled, allowed, navigate, toast]);

  return allowed;
}

export default function ConnectedBusinessesSettings() {
  const allowed = useOwnerGate();
  const navigate = useNavigate();
  if (!allowed) return null;
  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="cb-settings-page">
      <button type="button" onClick={() => navigate('/home')} data-testid="cb-settings-back"
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-4 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400">
        <ArrowLeft size={14} /> Back to dashboard
      </button>
      <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Connected Businesses</h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">Bakemill ERP integration — the API key is stored server-side and never shown in full.</p>
      <SettingsForm />
    </div>
  );
}

function SettingsForm() {
  const { token } = useAuth();
  const toast = useToast();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ api_url: '', api_key: '', api_key_header: 'X-API-Key', is_active: true });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = useCallback(async (resetForm) => {
    try {
      const c = await cbApi.settings(token);
      setCfg(c); setLoadError('');
      if (resetForm) setForm({ api_url: c.api_url || '', api_key: '', api_key_header: c.api_key_header || 'X-API-Key', is_active: c.is_active !== false });
    } catch (e) { setLoadError(e.message || 'Failed to load settings'); }
  }, [token]);

  useEffect(() => { reload(true); }, [reload]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const url = form.api_url.trim();
  const notHttps = url && !/^https:\/\//i.test(url);

  const save = async () => {
    setSaving(true);
    try {
      const body = { api_url: url, api_key_header: form.api_key_header.trim() || 'X-API-Key', is_active: form.is_active };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();
      await cbApi.patchSettings(token, body);
      toast.show('Connected Businesses settings saved', 'success');
      await reload(true);
    } catch (e) { toast.show(e.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      await cbApi.summary(token, true);
      toast.show('Connection successful — Bakemill ERP synced', 'success');
    } catch (e) { toast.show(`Connection failed: ${(await resolveSyncError(token, e)).detail}`, 'error'); }
    finally { setTesting(false); reload(false); }
  };

  if (!cfg && !loadError) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;
  if (loadError && !cfg) return <p className="text-sm text-red-600" data-testid="cb-settings-load-error">{loadError}</p>;

  return (
    <div className="glass-card rounded-3xl p-6 bg-white space-y-5" data-testid="cb-settings-form">
      <Field id="cb-api-url" label="API URL *" hint={notHttps ? 'Use an https:// URL — plain http sends the API key unencrypted.' : 'Must be an https:// endpoint.'} warn={notHttps}>
        <input id="cb-api-url" data-testid="cb-api-url-input" type="url" className="input-sm w-full" value={form.api_url} onChange={e => set('api_url', e.target.value)} placeholder="https://bakemill.in/api/external/business-summary" />
      </Field>
      <Field id="cb-api-key" label="API Key" hint={`Current key: ${cfg.api_key_masked || 'not set (server default in use)'} — leave blank to keep it.`}>
        <div className="flex gap-2">
          <input id="cb-api-key" data-testid="cb-api-key-input" type={showKey ? 'text' : 'password'} autoComplete="new-password" className="input-sm flex-1" value={form.api_key} onChange={e => set('api_key', e.target.value)} placeholder={cfg.api_key_masked || 'Enter API key'} />
          <button type="button" onClick={() => setShowKey(s => !s)} data-testid="cb-api-key-toggle" aria-label={showKey ? 'Hide API key' : 'Show API key'}
            className="flex items-center gap-1 px-3 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50">
            {showKey ? <EyeOff size={14} /> : <Eye size={14} />}{showKey ? 'Hide' : 'Show'}
          </button>
        </div>
      </Field>
      <Field id="cb-api-key-header" label="API Key Header name">
        <input id="cb-api-key-header" data-testid="cb-api-key-header-input" className="input-sm w-full" value={form.api_key_header} onChange={e => set('api_key_header', e.target.value)} placeholder="X-API-Key" />
      </Field>
      <div className="flex items-center justify-between">
        <span id="cb-active-label" className="text-sm font-semibold text-gray-700">Integration active</span>
        <button type="button" role="switch" aria-checked={form.is_active} aria-labelledby="cb-active-label" data-testid="cb-active-toggle" onClick={() => set('is_active', !form.is_active)}
          className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${form.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-5' : ''}`} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" onClick={save} disabled={saving || !url} data-testid="cb-save-btn"
          className="flex items-center gap-1.5 h-10 px-4 rounded-xl bg-indigo-500 text-white text-sm font-bold hover:bg-indigo-600 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button type="button" onClick={testConnection} disabled={testing} data-testid="cb-test-btn"
          className="flex items-center gap-1.5 h-10 px-4 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">
          {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />} Test Connection
        </button>
      </div>
      <SyncStatus cfg={cfg} />
    </div>
  );
}

function Field({ id, label, hint, warn, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className={`text-xs mt-1 ${warn ? 'text-amber-600' : 'text-gray-400'}`}>{hint}</p>}
    </div>
  );
}

function SyncStatus({ cfg }) {
  const st = cfg.last_sync_status;
  return (
    <div className="pt-4 border-t border-gray-100 text-sm space-y-1" data-testid="cb-sync-status">
      <p className="text-gray-600">
        Current key: <span className="font-mono font-semibold text-gray-900" data-testid="cb-masked-key">{cfg.api_key_masked || '—'}</span>
      </p>
      <p className="text-gray-600" title={cfg.last_synced_at ? fmtDateTime(cfg.last_synced_at) : undefined}>
        Last synced: <span className="font-semibold text-gray-900" data-testid="cb-last-synced">{cfg.last_synced_at ? `${fmtDateTime(cfg.last_synced_at)} (${fmtRelative(cfg.last_synced_at)})` : 'Never'}</span>
      </p>
      <p className="text-gray-600">
        Last sync status: <span data-testid="cb-last-sync-status" className={`font-bold ${st === 'success' ? 'text-emerald-600' : st === 'error' ? 'text-red-600' : 'text-gray-400'}`}>{st || '—'}</span>
      </p>
      {st === 'error' && cfg.last_sync_error && <p className="text-xs text-red-500 break-all" data-testid="cb-last-sync-error">{cfg.last_sync_error}</p>}
    </div>
  );
}
